/**
 * FleetOrderRowPendingOverlay Component
 *
 * An absolute-fill scrim rendered inside an existing FleetOrderRow while a
 * cancel or reschedule mutation syncs. It dims the row (semi-transparent white)
 * and shows a small shimmer strip + progress caption ("Wird storniert…" /
 * "Wird verschoben…") during `pending`, then a <SyncedCheckPulse/> once the
 * change lands. Because it fills the row and keeps pointer events, it also
 * blocks interaction with the (already gated) action buttons underneath.
 *
 * @module components/FleetOrderRowPendingOverlay
 */

import { StyleSheet, View, ViewStyle, TextStyle } from "react-native"
import Animated, { FadeIn, FadeOut } from "react-native-reanimated"

import { colors, spacing } from "@/theme"

import { SkeletonShimmer } from "./SkeletonShimmer"
import { SyncedCheckPulse } from "./SyncedCheckPulse"
import { Text } from "./Text"

export interface FleetOrderRowPendingOverlayProps {
  /**
   * Which mutation is in flight — drives the caption copy.
   */
  type: "cancel" | "reschedule"

  /**
   * "pending" while syncing (shimmer + caption), "synced" once the change
   * landed (check pulse).
   */
  phase: "pending" | "synced"
}

/**
 * FleetOrderRowPendingOverlay Component
 */
export function FleetOrderRowPendingOverlay({ type, phase }: FleetOrderRowPendingOverlayProps) {
  const caption = type === "cancel" ? "Wird storniert…" : "Wird verschoben…"

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
      style={$overlay}
      pointerEvents="auto"
    >
      <View style={$content}>
        {phase === "synced" ? (
          <SyncedCheckPulse />
        ) : (
          <>
            <SkeletonShimmer width={120} height={10} borderRadius={5} />
            <Text style={$caption}>{caption}</Text>
          </>
        )}
      </View>
    </Animated.View>
  )
}

// Styles
const $overlay: ViewStyle = {
  ...StyleSheet.absoluteFillObject,
  backgroundColor: "rgba(255, 255, 255, 0.55)",
  borderRadius: 14,
  alignItems: "center",
  justifyContent: "center",
  zIndex: 3,
}

const $content: ViewStyle = {
  alignItems: "center",
  gap: spacing.xs, // 8
}

const $caption: TextStyle = {
  fontSize: 13,
  fontWeight: "600",
  color: colors.textDim,
  letterSpacing: 0, // Workaround for React Native Web text node error
}
